import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { UploadCloud, X, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VALID_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const detectImageType = (filename) => {
  const base = filename.toLowerCase().replace(/\.[^.]+$/, '');
  if (/(^|[^a-z])(before|pre)([^a-z]|$)/.test(base)) return 'before';
  if (/(^|[^a-z])(after|post)([^a-z]|$)/.test(base)) return 'after';
  return '';
};

const matchEntry = (filename, entries) => {
  const n = norm(filename);
  for (const entry of entries) {
    const caseId = norm(entry.case_id);
    if (caseId && n.includes(caseId)) return entry.id;
  }
  for (const entry of entries) {
    const pname = norm(entry.patient_name);
    if (pname && pname.length >= 4 && n.includes(pname)) return entry.id;
  }
  return '';
};

export const BulkImageUploadDialog = ({ open, onClose, entries, token, onUploaded }) => {
  const [rows, setRows] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const addFiles = useCallback((fileList) => {
    const newRows = [];
    for (const file of Array.from(fileList)) {
      if (!VALID_TYPES.includes(file.type)) {
        toast.error(`${file.name}: only JPG, PNG or WebP allowed`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: exceeds 10 MB limit`);
        continue;
      }
      newRows.push({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
        entryId: matchEntry(file.name, entries),
        imageType: detectImageType(file.name),
        status: 'pending',
      });
    }
    if (newRows.length) setRows((prev) => [...prev, ...newRows]);
  }, [entries]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeRow = (id) => {
    setRows((prev) => {
      const row = prev.find((r) => r.id === id);
      if (row) URL.revokeObjectURL(row.preview);
      return prev.filter((r) => r.id !== id);
    });
  };

  const resetAndClose = () => {
    rows.forEach((r) => URL.revokeObjectURL(r.preview));
    setRows([]);
    onClose();
  };

  const readyCount = rows.filter((r) => r.entryId && r.imageType && r.status !== 'done').length;
  const unmatchedCount = rows.filter((r) => (!r.entryId || !r.imageType) && r.status !== 'done').length;

  const handleUploadAll = async () => {
    const toUpload = rows.filter((r) => r.entryId && r.imageType && r.status !== 'done');
    if (!toUpload.length) return;
    setUploading(true);
    let success = 0, failed = 0;
    for (const row of toUpload) {
      updateRow(row.id, { status: 'uploading' });
      try {
        const formData = new FormData();
        formData.append('file', row.file);
        await axios.post(
          `${API}/logbook/entries/${row.entryId}/images?image_type=${row.imageType}`,
          formData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        updateRow(row.id, { status: 'done' });
        success++;
      } catch (err) {
        updateRow(row.id, { status: 'error' });
        failed++;
      }
    }
    setUploading(false);
    if (success) toast.success(`${success} image${success > 1 ? 's' : ''} uploaded successfully`);
    if (failed) toast.error(`${failed} upload${failed > 1 ? 's' : ''} failed. Retry from the list.`);
    onUploaded();
  };

  const entryLabel = (entry) => `${entry.case_id} — ${entry.patient_name || 'N/A'} (${entry.procedure_type || 'Case'})`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetAndClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B2A4A] font-heading">Bulk Case Image Upload</DialogTitle>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-sm py-8 px-4 transition-colors cursor-pointer ${dragging ? 'border-[#C5A572] bg-[#C5A572]/5' : 'border-slate-300 hover:border-[#C5A572] hover:bg-slate-50'}`}
          onClick={() => document.getElementById('bulk-file-input').click()}
          data-testid="bulk-drop-zone"
        >
          <UploadCloud className={`h-8 w-8 mb-2 ${dragging ? 'text-[#C5A572]' : 'text-slate-400'}`} />
          <p className="text-sm text-slate-600 font-medium">Drag & drop multiple images here, or click to browse</p>
          <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP — max 10 MB each</p>
          <p className="text-[11px] text-slate-400 mt-2 text-center max-w-md">
            Tip: name files with the Case ID or patient name plus "before"/"after" (e.g. <span className="font-mono">CASE-1A2B3C4D_before.jpg</span>) to auto-match them to entries.
          </p>
          <input
            id="bulk-file-input"
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            data-testid="bulk-file-input"
          />
        </div>

        {rows.length > 0 && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-slate-500">
                {rows.length} file{rows.length > 1 ? 's' : ''} • <span className="text-green-600">{readyCount} ready</span>
                {unmatchedCount > 0 && <span className="text-amber-600"> • {unmatchedCount} need review</span>}
              </p>
              <Button variant="ghost" size="sm" className="text-xs text-slate-400 h-7" onClick={() => { rows.forEach((r) => URL.revokeObjectURL(r.preview)); setRows([]); }} data-testid="bulk-clear-all-btn">
                Clear all
              </Button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="bulk-file-list">
              {rows.map((row) => (
                <div key={row.id} className={`flex items-center gap-3 p-2 rounded-sm border ${row.status === 'done' ? 'border-green-200 bg-green-50' : row.status === 'error' ? 'border-red-200 bg-red-50' : row.entryId && row.imageType ? 'border-slate-200 bg-white' : 'border-amber-200 bg-amber-50'}`} data-testid="bulk-file-row">
                  <img src={row.preview} alt={row.file.name} className="w-12 h-12 object-cover rounded-sm border border-slate-200 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate" title={row.file.name}>{row.file.name}</p>
                    <div className="flex gap-2 mt-1.5">
                      <Select value={row.entryId} onValueChange={(v) => updateRow(row.id, { entryId: v })} disabled={row.status === 'done' || uploading}>
                        <SelectTrigger className="h-7 text-xs rounded-sm flex-1 min-w-0" data-testid="bulk-entry-select">
                          <SelectValue placeholder="Select case entry..." />
                        </SelectTrigger>
                        <SelectContent>
                          {entries.map((entry) => (
                            <SelectItem key={entry.id} value={entry.id} className="text-xs">{entryLabel(entry)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={row.imageType} onValueChange={(v) => updateRow(row.id, { imageType: v })} disabled={row.status === 'done' || uploading}>
                        <SelectTrigger className="h-7 text-xs rounded-sm w-24 flex-shrink-0" data-testid="bulk-type-select">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="before" className="text-xs">Before</SelectItem>
                          <SelectItem value="after" className="text-xs">After</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {row.status === 'done' ? (
                      <Badge className="bg-green-100 text-green-700 text-[10px] rounded-sm" data-testid="bulk-status-done"><CheckCircle2 className="h-3 w-3 mr-1" /> Uploaded</Badge>
                    ) : row.status === 'uploading' ? (
                      <Loader2 className="h-4 w-4 text-[#C5A572] animate-spin" />
                    ) : row.status === 'error' ? (
                      <Badge className="bg-red-100 text-red-700 text-[10px] rounded-sm">Failed</Badge>
                    ) : row.entryId && row.imageType ? (
                      <Badge className="bg-green-100 text-green-700 text-[10px] rounded-sm" data-testid="bulk-status-matched">Matched</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px] rounded-sm" data-testid="bulk-status-review"><AlertTriangle className="h-3 w-3 mr-1" /> Review</Badge>
                    )}
                    <button onClick={() => removeRow(row.id)} disabled={uploading} className="text-slate-400 hover:text-red-600 transition-colors" data-testid="bulk-remove-row-btn">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-2 border-t pt-4">
          <Button variant="outline" onClick={resetAndClose} className="rounded-sm" data-testid="bulk-close-btn">Close</Button>
          <Button
            onClick={handleUploadAll}
            disabled={uploading || readyCount === 0}
            className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
            data-testid="bulk-upload-all-btn"
          >
            {uploading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>) : `Upload ${readyCount || ''} Image${readyCount === 1 ? '' : 's'}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
